/**
 * The one meaning of "needs you" (ADR 0056 decision 6, R-HL-8): one pure selector over approvals,
 * asks and their dismissals that every surface reads (the drawer, Overview, the Inbox, the rail
 * and phone badges, the Sessions page, the notification centre, the palette, the assistant), and
 * that the hub can run on its own state, so what the hub counts is what a person sees.
 *
 * Two levels (Blocked is not the same as asked):
 * - blocked: the session cannot go on. A pending request (tool, question, plan, form; control
 *   requests held for a terminal included), a decided request that stopped its session and was
 *   not handled (R-HL-3), an assistant step waiting for approval, or a waiting session whose turn
 *   did not end on a question (it may be held at a prompt in its terminal). Leads, amber, counts.
 * - asked: the turn ended on a question and nothing is held. Listed below, neutral, counts for
 *   24 hours; after that it is an open question under Idle and stops counting.
 * A dismissed or snoozed asked question leaves the lists and the counts (setAside keeps it).
 * With the hub's answerer on Orgus (ORGU-81), a mission session's closing question is Orgus's to
 * answer and is not listed; pending requests are then only those an ask rule a person wrote made.
 */
import type { Id, Session, Ts } from '../index.ts';
import type { ApprovalAnswerer, ApprovalRequest } from './approvals.ts';
import { approvalBlockFor, approvalOutcomeOf } from './approvals.ts';
import { missionIdInTitle } from './missions.ts';
import type { AskLevel, AskSetAside } from './asks.ts';
import { ASK_OPEN_QUESTION_MS, askIdOf, askLevel, sessionAskOf, setAsideFor } from './asks.ts';

/** What kind of thing waits: a pending request, a request that stopped its session, an assistant step, a session. */
export type NeedsYouKind = 'request' | 'stopped' | 'step' | 'session';

/** An assistant step waiting for approval, as the selector needs it. */
export interface NeedsYouStep {
  id: Id;
  title: string;
  at: Ts;
}

export interface NeedsYouItem {
  /** The ask's id: the approval id for a request, the SessionAsk id (askIdOf) for a session. */
  askId: Id;
  level: AskLevel;
  kind: NeedsYouKind;
  sessionId: Id | null;
  /** The why, in one line: the question, the request's title. Empty when Orgus cannot show it. */
  reason: string;
  /** Since when it waits. */
  at: Ts;
  approval?: ApprovalRequest;
  session?: Session;
  step?: NeedsYouStep;
}

/** A session's place in Needs you: what its row, its pill and its group count read. */
export type NeedsYouSessionLevel = AskLevel | 'open' | 'set-aside';

export interface NeedsYou {
  /** Held asks, oldest first: lead, amber, count. */
  blocked: NeedsYouItem[];
  /** Asked questions under 24 hours old, oldest first: below blocked, neutral, count. */
  asked: NeedsYouItem[];
  /** Asked questions older than 24 hours: "Open question" under Idle, not counted. */
  open: NeedsYouItem[];
  /** Asked questions a person dismissed or snoozed, with the record; not counted. */
  setAside: { item: NeedsYouItem; record: AskSetAside }[];
  /** blocked + asked: the Inbox, the rail badge, the phone tab and Overview agree on it. */
  count: number;
  /** The oldest wait among what counts, or null. */
  oldest: Ts | null;
  /** Each session that waits on a person, by id. */
  bySession: Map<Id, NeedsYouSessionLevel>;
}

export interface NeedsYouInput {
  sessions: Iterable<Session>;
  /** state.ext.approvals.pending */
  pending?: readonly ApprovalRequest[];
  /** state.ext.approvals.blocking (requests that stopped a session, R-HL-3). */
  blocking?: readonly ApprovalRequest[];
  /** Assistant steps waiting for approval. */
  steps?: readonly NeedsYouStep[];
  /** state.ext.asks.setAside */
  setAside?: readonly AskSetAside[];
  /**
   * state.ext.approvals.answerer (ORGU-81). With 'orgus', a mission session's closing question is
   * Orgus's to answer ("Decide and continue"), so it is not asked of a person and does not count.
   */
  answerer?: ApprovalAnswerer;
  now: Ts;
}

const byAt = (a: NeedsYouItem, b: NeedsYouItem) => a.at - b.at;

/** A decided request that still stops its session: expired with the session parked, or denied; not handled, not worked past. */
export function stillBlocks(a: ApprovalRequest): boolean {
  return !!approvalOutcomeOf(a) && !a.dismissedAt && !a.laterWorkAt;
}

/** The one-line why of a waiting session: its closing question, else its last text. */
export function sessionReason(s: Pick<Session, 'ext' | 'lastText'>): string {
  const ask = sessionAskOf(s.ext);
  return (ask?.question ?? s.lastText ?? '').replace(/\s+/g, ' ').trim();
}

/** ORGU-81: Orgus answers this session's closing question, not a person: a mission session, with the answerer on Orgus. */
export function orgusAnswers(s: Pick<Session, 'title'>, answerer: ApprovalAnswerer | undefined): boolean {
  return answerer === 'orgus' && missionIdInTitle(s.title) !== null;
}

export function needsYou(input: NeedsYouInput): NeedsYou {
  const { now } = input;
  const blocked: NeedsYouItem[] = [];
  const asked: NeedsYouItem[] = [];
  const open: NeedsYouItem[] = [];
  const setAside: NeedsYou['setAside'] = [];
  const bySession = new Map<Id, NeedsYouSessionLevel>();

  const pending = (input.pending ?? []).filter((a) => a.state === 'pending');
  for (const a of pending) {
    blocked.push({ askId: a.id, level: 'blocked', kind: 'request', sessionId: a.sessionId, reason: a.title, at: a.createdAt, approval: a });
    bySession.set(a.sessionId, 'blocked');
  }
  const held = new Set(pending.map((a) => a.sessionId));
  const sessions = [...input.sessions];
  const byId = new Map(sessions.map((s) => [s.id, s]));
  const decided = [...pending, ...(input.blocking ?? [])];
  for (const a of input.blocking ?? []) {
    if (!stillBlocks(a)) continue;
    // The Inbox's rule (approvalBlockFor): a session that is working or waiting again is not
    // stopped, and only its newest block shows. Counting more would be Needs you with nothing
    // to act on (dogfood, 18 Oct 2026: a denial with a note, the session carried on).
    const s = byId.get(a.sessionId);
    if (s && approvalBlockFor(s, decided, now)?.approval.id !== a.id) continue;
    blocked.push({ askId: a.id, level: 'blocked', kind: 'stopped', sessionId: a.sessionId, reason: a.title, at: a.decidedAt ?? a.createdAt, approval: a });
    if (!bySession.has(a.sessionId)) bySession.set(a.sessionId, 'blocked');
  }
  for (const st of input.steps ?? []) blocked.push({ askId: st.id, level: 'blocked', kind: 'step', sessionId: null, reason: st.title, at: st.at, step: st });

  for (const s of sessions) {
    // A session held at a request counts once, as the request.
    if (s.status !== 'waiting' || held.has(s.id)) continue;
    const level = askLevel(s, false);
    if (!level) continue;
    const ask = sessionAskOf(s.ext);
    const item: NeedsYouItem = { askId: askIdOf(s), level, kind: 'session', sessionId: s.id, reason: sessionReason(s), at: ask?.askedAt || s.lastActivity, session: s };
    if (level === 'blocked') {
      blocked.push(item);
      bySession.set(s.id, 'blocked');
      continue;
    }
    if (orgusAnswers(s, input.answerer)) continue;
    const record = setAsideFor(input.setAside ?? [], item.askId, now);
    if (record) {
      setAside.push({ item, record });
      if (!bySession.has(s.id)) bySession.set(s.id, 'set-aside');
    } else if (now - item.at >= ASK_OPEN_QUESTION_MS) {
      open.push(item);
      if (!bySession.has(s.id)) bySession.set(s.id, 'open');
    } else {
      asked.push(item);
      if (!bySession.has(s.id)) bySession.set(s.id, 'asked');
    }
  }

  blocked.sort(byAt);
  asked.sort(byAt);
  open.sort(byAt);
  const counted = [...blocked, ...asked];
  const oldest = counted.reduce<Ts | null>((m, i) => (m == null || i.at < m ? i.at : m), null);
  return { blocked, asked, open, setAside, count: counted.length, oldest, bySession };
}

/** Whether a session with this level reads Needs you or Asked (counts), not Idle. */
export function countsAsWaiting(level: NeedsYouSessionLevel | null | undefined): boolean {
  return level === 'blocked' || level === 'asked';
}
